import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compact, inr } from "@/lib/format";
import { StatCard, DaysToggle, Skeleton } from "@/components/Shared";
import { FeeTypeMatrix } from "@/components/FeeTypeMatrix";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Fees() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard/fees?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data) return <div className="space-y-6"><Skeleton className="h-28" /><Skeleton className="h-72" /></div>;

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Real Amazon fees from your settlement data — last {days} days.</p>
        <DaysToggle value={days} onChange={setDays} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Settled Revenue" value={compact(data.summary.revenue)} sub="principal charged" testId="fees-revenue" />
        <StatCard label="Total Fees" value={compact(data.summary.total_fees)} sub="all Amazon fee types" testId="fees-total" />
        <StatCard label="Fee % of Revenue" value={`${data.summary.fee_pct}%`} sub="effective fee rate" testId="fees-pct" />
      </div>
      {data.types.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">No fee events in this window yet — fees appear after Amazon posts settlement events (sync runs daily).</p>
      ) : (
        <>
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="fees-chart">
            <h3 className="font-heading text-base font-semibold tracking-tight mb-5">Fees by type</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.types} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} width={160} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => [inr(v), "Amount"]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="amount" fill="#D97706" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="fees-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  {["Fee Type", "Amount", "% of Total Fees"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.types.map((t) => (
                  <tr key={t.type} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`fee-row-${t.type}`}>
                    <td className="px-4 py-3 font-medium">{t.type}</td>
                    <td className="px-4 py-3 font-semibold text-red-600">{inr(t.amount)}</td>
                    <td className="px-4 py-3">{t.pct_of_fees}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <FeeTypeMatrix />
    </div>
  );
}
